import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download, Loader2, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CAT_ICONS } from "../lib/icons";
import { ItemsTable } from "../components/items/ItemsTable";
import { ItemFilters } from "../components/items/ItemFilters";
import { ItemFormDialog } from "../components/items/ItemFormDialog";
import { DeleteDialog } from "../components/items/DeleteDialog";
import { ImportDialog } from "../components/items/ImportDialog";
import { PdfButton } from "../components/PdfButton";

const PAGE = 25;

export default function CategoryPage() {
  const { cat: key } = useParams();
  const { meta } = useOutletContext();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const cat = meta.categories.find((c) => c.key === key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kondisi, setKondisi] = useState("ALL");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const polda = sp.get("polda") || "ALL";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/items/${key}`);
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    load();
    setQ("");
    setKondisi("ALL");
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [q, kondisi, polda, key]);

  const base = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => (polda === "ALL" || it.polda === polda) &&
      (!s || Object.values(it.data).join(" ").toLowerCase().includes(s) || (it.polda || "").toLowerCase().includes(s)));
  }, [items, q, polda]);
  const filtered = useMemo(() => base.filter((it) => kondisi === "ALL" || it.kondisi_kat === kondisi), [base, kondisi]);

  if (!cat) return <Navigate to="/dashboard" replace />;
  const Icon = CAT_ICONS[cat.key];
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const showPolda = cat.polda_scoped && user.role !== "polda";
  const poldaOptions = [...new Set([...meta.polda_list, ...items.map((i) => i.polda).filter(Boolean)])];

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadExport(cat.key, polda !== "ALL" ? polda : undefined);
    } catch (e) {
      toast.error("Gagal mengekspor data");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-[1500px]" data-testid={`category-page-${cat.key}`}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <div className="eyebrow flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{cat.polda_scoped && user.role === "polda" ? user.polda : cat.unit || "Data Inventaris"}</div>
          <h1 className="mt-2 font-heading font-black tracking-tight text-3xl sm:text-4xl" data-testid="category-title">{cat.label}</h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="category-count">{filtered.length} dari {items.length} entri</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {cat.polda_scoped && (
            <Button variant="outline" onClick={() => setImporting(true)} data-testid="import-excel-button" className="col-span-2 sm:col-span-1 active:scale-[0.98] transition-transform">
              <Upload className="h-4 w-4" /> Impor Excel
            </Button>
          )}
          <PdfButton params={{ scope: "category", cat: cat.key, ...(polda !== "ALL" ? { polda } : {}) }} />
          <Button variant="outline" onClick={doExport} disabled={exporting} data-testid="export-excel-button" className="active:scale-[0.98] transition-transform">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Excel
          </Button>
          <Button onClick={() => setEditing({})} data-testid="add-item-button" className="col-span-2 order-first sm:order-none h-11 sm:h-9 active:scale-[0.98] transition-transform">
            <Plus className="h-4 w-4" /> Tambah Data
          </Button>
        </div>
      </div>
      <ItemFilters
        cat={cat} q={q} setQ={setQ} kondisi={kondisi} setKondisi={setKondisi} base={base}
        showPolda={showPolda} polda={polda} poldaOptions={poldaOptions}
        setPolda={(p) => setSp(p === "ALL" ? {} : { polda: p })}
      />
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <ItemsTable
          cat={cat} rows={filtered.slice((page - 1) * PAGE, page * PAGE)} start={(page - 1) * PAGE}
          showPolda={showPolda} onEdit={setEditing} onDelete={setDeleting}
          page={page} pages={pages} setPage={setPage}
          onAdd={() => setEditing({})}
        />
      )}
      <ItemFormDialog
        open={!!editing} item={editing} cat={cat} meta={meta} user={user}
        defaultPolda={polda !== "ALL" ? polda : ""}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}
      />
      <DeleteDialog
        item={deleting} cat={cat} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }}
      />
      {cat.polda_scoped && (
        <ImportDialog
          open={importing} cat={cat} meta={meta} user={user} defaultPolda={polda !== "ALL" ? polda : ""}
          onClose={() => setImporting(false)} onDone={load}
        />
      )}
    </div>
  );
}
